/**
 * accessControl.js — SINGLE SOURCE OF TRUTH for sidebar / nav / role access.
 *
 * Everything in the app — desktop Sidebar, MobileNav, RoleGate route guard,
 * Tech Control Panel — reads from this file. NO other file is allowed to
 * hardcode role lists or sidebar items. This file is the one you edit when
 * you add a feature or change visibility.
 *
 * Structure:
 *   FINAL_ROLES                      → 22 canonical role keys.
 *   COMMON_ITEMS                     → items every authenticated user sees.
 *   DROPDOWNS                        → 22 dropdowns, each a list of items.
 *   ROLE_TO_DROPDOWNS                → which dropdowns each role gets.
 *   ROLE_GETS_ISSUE_REPORTING        → managers/office/on-field, not staff.
 *   getNavForRole(role, overrides)   → returns the resolved nav for a role,
 *                                       merging Tech Control Panel overrides.
 */

import {
  AlertCircle, AlertTriangle, ArrowLeftRight, BarChart3, Bell, BellRing,
  BookOpen, Briefcase, Building, Building2, Calendar, CalendarCheck,
  CalendarDays, CalendarOff, CheckCircle, ClipboardCheck, ClipboardEdit,
  Clock, ClipboardList, DollarSign, FileText, Fingerprint, FolderArchive,
  FolderKanban, Gift, GraduationCap, HardDrive, Home, Inbox, LayoutDashboard,
  LayoutGrid, ListChecks, MessageSquare, Package, PenTool, Receipt,
  ScrollText, Settings, Share2, Shield, ShieldAlert, Star, Target,
  Thermometer, TrendingUp, Trophy, Truck, User, UserCheck, UserPlus,
  Users, Wallet, Wrench,
} from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────
// Final roles — anything else in the DB must be migrated. See
// /app/backend/scripts/migrate_roles.py for the mapping.
// ──────────────────────────────────────────────────────────────────────
export const FINAL_ROLES = [
  'ceo', 'md', 'tech_support',
  'operations_manager', 'franchise_manager', 'hr_manager',
  'accounts_manager', 'accounts_payables',
  'purchase_manager', 'warehouse_manager', 'warehouse_admin',
  'store_manager',
  'sales_manager', 'marketing_manager',
  'project_manager', 'site_supervisor',
  'warehouse_driver', 'warehouse_staff',
  'import_manager', 'office_administrator',
  'store_staff',
];

export const ROLE_LABELS = {
  ceo: 'CEO', md: 'MD', tech_support: 'Tech Support',
  operations_manager: 'Operations Manager',
  franchise_manager: 'Franchise Manager',
  hr_manager: 'HR Manager',
  accounts_manager: 'Accounts Manager',
  accounts_payables: 'Accounts Payables',
  purchase_manager: 'Purchase Manager',
  warehouse_manager: 'Warehouse Manager',
  warehouse_admin: 'Warehouse Admin',
  store_manager: 'Store Manager',
  sales_manager: 'Sales Manager',
  marketing_manager: 'Marketing Manager',
  project_manager: 'Project Manager',
  site_supervisor: 'Site Supervisor',
  warehouse_driver: 'Warehouse Driver',
  warehouse_staff: 'Warehouse Staff',
  import_manager: 'Import Manager',
  office_administrator: 'Office Administrator',
  store_staff: 'Store Staff',
};

// ──────────────────────────────────────────────────────────────────────
// Common items — every authenticated user sees these (subject to overrides).
// Note: Reminders has been consolidated into the Calendar page (May 2026) —
// reminders surface as events on the calendar grid, and to-dos can attach a
// reminder which appears at the chosen time. The /reminders route remains
// reachable directly but is no longer a top-level sidebar entry.
// ──────────────────────────────────────────────────────────────────────
export const COMMON_ITEMS = [
  { key: 'my_dashboard',  path: '/admin/my-dashboard', icon: LayoutGrid,    label: 'My Dashboard' },
  { key: 'tasks',         path: '/task-management',    icon: CheckCircle,   label: 'Tasks' },
  { key: 'calendar',      path: '/calendar',           icon: Calendar,      label: 'Calendar' },
  { key: 'my_performance',path: '/my-performance',     icon: Star,          label: 'My Performance' },
  // Roles below don't need these — keep their sidebar focused on day-to-day ops.
  { key: 'my_training',   path: '/training-hub',       icon: GraduationCap, label: 'Training',     hideForRoles: ['store_staff'] },
  { key: 'my_shifts',     path: '/roster/my-shifts',   icon: CalendarDays,  label: 'My Shifts',    hideForRoles: ['store_staff'] },
  { key: 'book_meeting',  path: '/book-meeting',       icon: CalendarCheck, label: 'Book Meeting', hideForRoles: ['store_staff'] },
  { key: 'chat',          path: '/chat',               icon: MessageSquare, label: 'Chat' },
  { key: 'resources',     path: '/resources',          icon: BookOpen,      label: 'Resources' },
  { key: 'organization',  path: '/organization',       icon: Building2,     label: 'Organization' },
  { key: 'policies',      path: '/policies',           icon: ScrollText,    label: 'Policies' },
  { key: 'profile',       path: '/profile',            icon: User,          label: 'Profile' },
];

// Issue Reporting — visible to managers/office/on-field, NOT store_staff.
export const ISSUE_REPORTING_ITEM = {
  key: 'issue_reporting', path: '/issue-reporting', icon: AlertCircle, label: 'Issue Reporting',
};
export const ROLES_WITH_ISSUE_REPORTING = FINAL_ROLES.filter(r => r !== 'store_staff');

// ──────────────────────────────────────────────────────────────────────
// Dropdowns. Each dropdown has a key, label, icon, and ordered items.
// ──────────────────────────────────────────────────────────────────────
export const DROPDOWNS = {
  leadership: {
    label: 'Leadership', icon: LayoutDashboard,
    items: [
      { key: 'ops_control',     path: '/admin',                icon: LayoutDashboard, label: 'Operations Control' },
      { key: 'approvals',       path: '/approvals',            icon: Inbox,           label: 'Approvals' },
      { key: 'staff_performance', path: '/staff-performance',  icon: Star,            label: 'Staff Performance' },
      { key: 'team_dashboards', path: '/admin/team-dashboards', icon: Users,          label: 'Team Dashboards' },
      { key: 'projects',        path: '/projects',             icon: FolderKanban,    label: 'Projects' },
      { key: 'p2p',             path: '/purchase-workflow',    icon: Receipt,         label: 'Purchase to Payment' },
      { key: 'franchise_invoices', path: '/franchise-invoices', icon: Receipt,        label: 'Franchise Invoices' },
      { key: 'store_bidding',   path: '/store-bidding',        icon: TrendingUp,      label: 'Store Bidding' },
      { key: 'org_structure',   path: '/admin/org-chart',      icon: Building2,       label: 'Org Structure' },
      { key: 'analytics',       path: '/admin/analytics',      icon: BarChart3,       label: 'Analytics' },
      { key: 'finance_overview', path: '/accounts',            icon: Wallet,          label: 'Finance Overview' },
    ],
  },

  operations: {
    label: 'Operations', icon: Shield,
    items: [
      { key: 'approvals',           path: '/approvals',                icon: Inbox,           label: 'Approvals' },
      { key: 'staff_performance',   path: '/staff-performance',        icon: Star,            label: 'Staff Performance' },
      { key: 'compliance',          path: '/compliance',               icon: Shield,          label: 'Compliance' },
      { key: 'compliance_expiry',   path: '/admin/compliance',         icon: AlertTriangle,   label: 'Compliance Expiry' },
      { key: 'compliance_tracker',  path: '/admin/compliance-tracker', icon: ListChecks,      label: 'Compliance Tracker' },
      { key: 'store_checklists',    path: '/store-checklists',         icon: ClipboardCheck,  label: 'Store Checklists' },
      { key: 'inspections',         path: '/inspections',              icon: ClipboardList,   label: 'Inspection Results' },
      { key: 'inspection_new',      path: '/inspections/new',          icon: ClipboardEdit,   label: 'New Inspection' },
      { key: 'incidents',           path: '/incidents',                icon: ShieldAlert,     label: 'Incidents' },
      { key: 'feedback',            path: '/feedback',                 icon: MessageSquare,   label: 'Feedback' },
      { key: 'scorecard',           path: '/admin/scorecard',          icon: Trophy,          label: 'Scorecard' },
      { key: 'store_expiry',        path: '/admin/store-expiry-dashboard', icon: Package,     label: 'Store Expiry Dashboard' },
      { key: 'points',              path: '/points-redemption',        icon: Gift,            label: 'Points & Redemption' },
      { key: 'corrective_actions',  path: '/admin/corrective-actions', icon: AlertTriangle,   label: 'Actions' },
      { key: 'manager_reports',     path: '/manager/reports',          icon: FileText,        label: 'Manager Reports' },
      { key: 'team_scoring',        path: '/team-scoring',             icon: Star,            label: 'Team Scoring' },
    ],
  },

  tech: {
    label: 'Tech', icon: Wrench,
    items: [
      { key: 'user_management',    path: '/admin/user-management',     icon: UserPlus,    label: 'User Management' },
      { key: 'tech_control_panel', path: '/admin/tech-control-panel',  icon: Wrench,      label: 'Access Control' },
      { key: 'app_settings',       path: '/admin/settings',            icon: Settings,    label: 'App Settings' },
      { key: 'form_builder',       path: '/admin/forms',               icon: ClipboardEdit,label:'Form Builder' },
      { key: 'onboarding_setup',   path: '/admin/onboarding-wizard',   icon: UserCheck,   label: 'Onboarding Setup' },
      { key: 'bulk_onboarding',    path: '/admin/bulk-onboarding',     icon: Users,       label: 'Bulk Onboarding' },
      { key: 'voice_settings',     path: '/admin/voice-settings',      icon: Settings,    label: 'Voice Settings' },
      { key: 'system_health',      path: '/admin/system-health',       icon: Shield,      label: 'System Health' },
      { key: 'records',            path: '/admin/records',             icon: FolderArchive,label:'Records' },
    ],
  },

  franchise: {
    label: 'Franchise', icon: Building2,
    items: [
      { key: 'franchise_dashboard', path: '/franchise',                 icon: Building2,   label: 'Franchise Dashboard' },
      { key: 'stores',              path: '/franchise/stores',          icon: Building,    label: 'Stores' },
      { key: 'onboarding_tracker',  path: '/franchise/onboarding',      icon: UserCheck,   label: 'Onboarding Tracker' },
      { key: 'opening_timeline',    path: '/franchise/opening-timeline', icon: Calendar,   label: 'Opening Timeline' },
      { key: 'franchise_financials',path: '/franchise/financials',      icon: BarChart3,   label: 'Franchise Financials' },
      { key: 'franchise_invoices',  path: '/franchise-invoices',        icon: Receipt,     label: 'Franchise Invoices' },
      { key: 'budget_settings',     path: '/franchise/budgets',         icon: Target,      label: 'Budget Settings' },
      { key: 'compliance',          path: '/franchise/compliance',      icon: Shield,      label: 'Compliance' },
      { key: 'store_checklists',    path: '/store-checklists',          icon: ClipboardCheck, label: 'Store Checklists' },
      { key: 'support_issues',      path: '/franchise/issues',          icon: AlertCircle, label: 'Support Issues' },
      { key: 'store_expiry',        path: '/admin/store-expiry-dashboard', icon: Package,  label: 'Store Expiry Dashboard' },
    ],
  },

  hr: {
    label: 'HR', icon: Users,
    items: [
      { key: 'hr_dashboard',     path: '/admin/hr-dashboard',          icon: Home,         label: 'HR Dashboard' },
      { key: 'onboarding',       path: '/admin/onboarding-wizard',     icon: UserCheck,    label: 'Onboarding' },
      { key: 'attendance',       path: '/admin/attendance',            icon: Fingerprint,  label: 'Attendance' },
      { key: 'roster',           path: '/admin/roster',                icon: CalendarDays, label: 'Roster' },
      { key: 'roster_approval',  path: '/admin/roster-approval',       icon: CheckCircle,  label: 'Roster Approval' },
      { key: 'shift_approvals',  path: '/admin/shift-approvals',       icon: CheckCircle,  label: 'Shift Approvals' },
      { key: 'unavailability',   path: '/admin/unavailability-approval', icon: CalendarOff, label:'Unavailability Approvals' },
      { key: 'time_clock',       path: '/admin/time-clock',            icon: Fingerprint,  label: 'Time Clock' },
      { key: 'kpi',              path: '/admin/kpi',                   icon: Star,         label: 'KPI' },
      { key: 'recognition',      path: '/admin/recognition',           icon: Trophy,       label: 'Recognition' },
      { key: 'training',         path: '/admin/training',              icon: GraduationCap,label: 'Training' },
      { key: 'training_hub_admin', path: '/admin/training-hub',         icon: BookOpen,     label: 'Training Hub (Modules)' },
      { key: 'training_compliance', path: '/admin/training-compliance', icon: BarChart3,    label: 'Training Compliance' },
      { key: 'training_approvals',path: '/trm-approvals',              icon: CheckCircle,  label: 'Training Approvals' },
      { key: 'team_structure',   path: '/admin/team-structure',        icon: Users,        label: 'Team Structure' },
      { key: 'manager_reports',  path: '/manager/reports',             icon: FileText,     label: 'Manager Reports' },
      { key: 'team_scoring',     path: '/team-scoring',                icon: Star,         label: 'Team Scoring' },
      { key: 'concerns',         path: '/admin/concerns',              icon: AlertCircle,  label: 'Concerns' },
      { key: 'incidents',        path: '/incidents',                   icon: ShieldAlert,  label: 'Incident Management' },
    ],
  },

  accounts_manager: {
    label: 'Accounts', icon: Wallet,
    items: [
      { key: 'accounts_dashboard', path: '/accounts',                 icon: Wallet,    label: 'Accounts Dashboard' },
      { key: 'franchise_invoices', path: '/franchise-invoices',       icon: Receipt,   label: 'Franchise Invoices' },
      { key: 'p2p',                path: '/purchase-workflow',         icon: Receipt,   label: 'Purchase to Payment' },
      { key: 'supplier_recon',     path: '/supplier-reconciliation',  icon: FileText,  label: 'Supplier Reconciliation' },
      { key: 'gtp_bills',          path: '/office-admin/expenses',    icon: DollarSign,label: 'GTP Bills' },
      // `receivables` and `aged_reports` removed — pointed to pages that
      // don't exist yet. Will be re-added when the backing pages ship.
      { key: 'tax_lodgements',     path: '/admin/tax-lodgements',     icon: FileText,  label: 'Tax Lodgements' },
      { key: 'bas',                path: '/bas-lodgments',            icon: FileText,  label: 'BAS Lodgments' },
      { key: 'ownership',          path: '/ownership-structure',      icon: Share2,    label: 'Ownership Structure' },
      { key: 'fin_docs',           path: '/admin/financial-docs',     icon: FolderArchive, label: 'Financial Documents' },
      { key: 'payroll_overview',   path: '/admin/payroll-dashboard',  icon: DollarSign,label: 'Payroll Overview' },
    ],
  },

  accounts_payables: {
    label: 'Payables', icon: Wallet,
    items: [
      { key: 'p2p',            path: '/purchase-workflow',       icon: Receipt,    label: 'Purchase to Payment' },
      { key: 'supplier_recon', path: '/supplier-reconciliation', icon: FileText,   label: 'Supplier Reconciliation' },
      { key: 'gtp_bills',      path: '/office-admin/expenses',   icon: DollarSign, label: 'GTP Bills' },
      // `payables`, `receivables`, `aged_reports` removed — targets don't
      // exist yet. Will be re-added when those dashboards ship.
    ],
  },

  purchase: {
    label: 'Purchase', icon: Receipt,
    items: [
      { key: 'inventory_dashboard', path: '/inventory',           icon: LayoutDashboard, label: 'Inventory Dashboard' },
      { key: 'purchase_orders',     path: '/purchase-workflow',   icon: Receipt,         label: 'Purchase Orders' },
      // `price_check` removed — page doesn't exist yet.
      { key: 'suppliers',           path: '/inventory/suppliers', icon: Truck,           label: 'Suppliers' },
      { key: 'store_bidding',       path: '/store-bidding',       icon: TrendingUp,      label: 'Store Bidding' },
      { key: 'customer_demands',    path: '/purchase/demands',    icon: Package,         label: 'Customer Demands' },
      { key: 'vendors',             path: '/vendors',             icon: Truck,           label: 'Vendors' },
      { key: 'product_catalog',     path: '/inventory/products',  icon: Package,         label: 'Product Catalog' },
    ],
  },

  warehouse_manager: {
    label: 'Warehouse', icon: Truck,
    items: [
      { key: 'receiving',          path: '/warehouse/receiving',         icon: Truck,         label: 'Receiving' },
      { key: 'dispatch',           path: '/warehouse/dispatch',          icon: Calendar,      label: 'Dispatch' },
      { key: 'inventory_dashboard',path: '/inventory',                   icon: LayoutDashboard,label:'Inventory Dashboard' },
      { key: 'products',           path: '/inventory/products',          icon: Package,       label: 'Products' },
      { key: 'categories',         path: '/inventory/categories',        icon: LayoutGrid,    label: 'Categories' },
      { key: 'suppliers',          path: '/inventory/suppliers',         icon: Truck,         label: 'Suppliers' },
      { key: 'customers',          path: '/inventory/customers',         icon: Users,         label: 'Customers' },
      { key: 'stock_adjustments',  path: '/inventory/stock-adjustments', icon: Settings,      label: 'Stock Adjustments' },
      { key: 'stock_takes',        path: '/inventory/stock-takes',       icon: ClipboardCheck,label: 'Stock Takes' },
      { key: 'credit_notes',       path: '/inventory/credit-notes',      icon: FileText,      label: 'Credit Notes' },
      { key: 'mobile_scanner',     path: '/inventory/scanner',           icon: BarChart3,     label: 'Mobile Scanner' },
      { key: 'warehouse_reports',  path: '/warehouse/reports',           icon: FileText,      label: 'Warehouse Reports' },
      { key: 'driver_reports',     path: '/admin/driver-reports',        icon: Truck,         label: 'Driver Reports' },
      { key: 'product_expiry',     path: '/expiry-list',                 icon: AlertTriangle, label: 'Product Expiry List' },
      { key: 'report_expiry',      path: '/expiry-report',               icon: ClipboardEdit, label: 'Report Expiry' },
    ],
  },

  warehouse_admin: {
    label: 'Warehouse', icon: Truck,
    items: [
      { key: 'dispatch',           path: '/warehouse/dispatch',          icon: Calendar,      label: 'Dispatch' },
      { key: 'inventory_dashboard',path: '/inventory',                   icon: LayoutDashboard,label:'Inventory Dashboard' },
      { key: 'products',           path: '/inventory/products',          icon: Package,       label: 'Products' },
      { key: 'categories',         path: '/inventory/categories',        icon: LayoutGrid,    label: 'Categories' },
      { key: 'suppliers',          path: '/inventory/suppliers',         icon: Truck,         label: 'Suppliers' },
      { key: 'customers',          path: '/inventory/customers',         icon: Users,         label: 'Customers' },
      { key: 'stock_adjustments',  path: '/inventory/stock-adjustments', icon: Settings,      label: 'Stock Adjustments' },
      { key: 'stock_takes',        path: '/inventory/stock-takes',       icon: ClipboardCheck,label: 'Stock Takes' },
      { key: 'credit_notes',       path: '/inventory/credit-notes',      icon: FileText,      label: 'Credit Notes' },
      { key: 'warehouse_reports',  path: '/warehouse/reports',           icon: FileText,      label: 'Warehouse Reports' },
      { key: 'driver_reports',     path: '/admin/driver-reports',        icon: Truck,         label: 'Driver Reports' },
      { key: 'product_expiry',     path: '/expiry-list',                 icon: AlertTriangle, label: 'Product Expiry List' },
    ],
  },

  store_manager: {
    label: 'Store Operations', icon: Building,
    items: [
      { key: 'store_ordering',  path: '/store/order',     icon: Package,       label: 'Store Ordering' },
      { key: 'my_orders',       path: '/store/orders',    icon: Receipt,       label: 'My Orders' },
      { key: 'product_catalog', path: '/store/products',  icon: Package,       label: 'Product Catalog' },
      { key: 'store_bidding',   path: '/store-bidding',   icon: TrendingUp,    label: 'Store Bidding' },
      { key: 'points',          path: '/points-redemption', icon: Gift,        label: 'Points & Redemption' },
      { key: 'store_grade',     path: '/my-store-grade',  icon: Trophy,        label: 'Store Grade' },
      { key: 'store_checklists',path: '/store-checklists',icon: ClipboardCheck,label: 'Store Checklists' },
      { key: 'store_compliance',path: '/compliance',      icon: Shield,        label: 'Store Compliance' },
      { key: 'franchise_invoices',path: '/franchise-invoices', icon: Receipt,  label: 'Franchise Invoices', requiresFranchiseStore: true },
      { key: 'incidents',       path: '/incidents',       icon: ShieldAlert,   label: 'Incident Management' },
      { key: 'unavailability',  path: '/admin/unavailability-approval', icon: CalendarOff, label: 'Unavailability Approvals' },
      { key: 'product_expiry',  path: '/expiry-list',     icon: AlertTriangle, label: 'Product Expiry List' },
      { key: 'report_expiry',   path: '/expiry-report',   icon: ClipboardEdit, label: 'Report Expiry' },
      { key: 'store_expiry',    path: '/admin/store-expiry-dashboard', icon: Package, label: 'Store Expiry Dashboard' },
    ],
  },

  grocery_manager: {
    label: 'Store Operations (deprecated)', icon: Building,
    // Kept only so any stale role override referencing 'grocery_manager'
    // doesn't crash getNavForRole. Role itself is removed from FINAL_ROLES.
    items: [],
  },

  sales: {
    label: 'Sales', icon: TrendingUp,
    items: [
      { key: 'customers',      path: '/inventory/customers', icon: Users,        label: 'Customers' },
      // `sales_dashboard` removed — no page backs /sales. Sales CEO + pipeline cover the need.
      { key: 'sales_pipeline', path: '/sales-pipeline',      icon: TrendingUp,   label: 'Sales Pipeline' },
      { key: 'content_planner',path: '/content-planner',     icon: CalendarCheck,label: 'Content Planner' },
    ],
  },

  marketing: {
    label: 'Marketing', icon: PenTool,
    items: [
      { key: 'campaigns',      path: '/marketing/campaigns', icon: Briefcase,    label: 'Campaigns' },
      { key: 'social',         path: '/marketing/social',    icon: Share2,       label: 'Social Media' },
      { key: 'content',        path: '/marketing/content',   icon: PenTool,      label: 'Content' },
      { key: 'analytics',      path: '/marketing/analytics', icon: BarChart3,    label: 'Analytics' },
      { key: 'content_planner',path: '/content-planner',     icon: CalendarCheck,label: 'Content Planner' },
      // `marketing_reports` removed — page not built yet.
    ],
  },

  project_manager: {
    label: 'Projects', icon: FolderKanban,
    items: [
      { key: 'all_projects',     path: '/projects',         icon: FolderKanban,label: 'All Projects' },
      { key: 'new_project',      path: '/projects/new',     icon: FolderKanban,label: 'New Project' },
      { key: 'project_dashboard',path: '/pm/dashboard',     icon: LayoutDashboard, label: 'Project Dashboard' },
      { key: 'expense_dashboard',path: '/admin/expenses',   icon: DollarSign,  label: 'Expense Dashboard' },
      { key: 'task_management',  path: '/task-management',  icon: CheckCircle, label: 'Task Management' },
      // `project_reports` removed — page not built yet.
    ],
  },

  site_supervisor: {
    label: 'Projects', icon: FolderKanban,
    items: [
      { key: 'my_projects',     path: '/projects',        icon: FolderKanban, label: 'My Projects' },
      { key: 'site_reports',    path: '/reporting',       icon: FileText,     label: 'Site Reports' },
      { key: 'task_management', path: '/task-management', icon: CheckCircle,  label: 'Task Management' },
      { key: 'expense_dashboard',path:'/admin/expenses',  icon: DollarSign,   label: 'Expense Dashboard' },
    ],
  },

  driver: {
    label: 'Driver', icon: Truck,
    items: [
      { key: 'my_deliveries', path: '/deliveries',     icon: Truck,        label: 'My Deliveries' },
      { key: 'driver_logbook',path: '/driver-logbook', icon: FileText,     label: 'Driver Logbook' },
      { key: 'vehicle_check', path: '/vehicle-check',  icon: CheckCircle,  label: 'Vehicle Check' },
      { key: 'routes',        path: '/routes',         icon: Building,     label: 'Routes' },
      { key: 'navigation',    path: '/navigation',     icon: Building,     label: 'Navigation' },
    ],
  },

  imports: {
    label: 'Imports', icon: Truck,
    items: [
      { key: 'import_pipeline',path: '/import-pipeline',icon: TrendingUp,label: 'Import Pipeline' },
      { key: 'imports',        path: '/imports',        icon: Package,   label: 'Imports' },
      { key: 'shipments',      path: '/shipments',      icon: Truck,     label: 'Shipments' },
      { key: 'customs',        path: '/customs',        icon: FileText,  label: 'Customs' },
      { key: 'vendors',        path: '/vendors',        icon: Truck,     label: 'Vendors' },
    ],
  },

  staff: {
    label: 'Staff', icon: User,
    items: [
      { key: 'demands_hub',    path: '/my-demands',     icon: Package,      label: 'Demands' },
      { key: 'incidents_hub',  path: '/my-incidents',   icon: ShieldAlert,  label: 'Incidents' },
      { key: 'feedback_hub',   path: '/my-feedback',    icon: MessageSquare,label: 'Feedback' },
      { key: 'report_expiry',  path: '/expiry-report',  icon: ClipboardEdit,label: 'Report Expiry' },
      { key: 'store_expiry',   path: '/admin/store-expiry-dashboard', icon: Package, label: 'Store Expiry Dashboard' },
    ],
  },

  office: {
    label: 'Office', icon: Briefcase,
    items: [
      { key: 'site_control',  path: '/site-control',          icon: Building2, label: 'Site Control & Renewals' },
      { key: 'gtp_bills',     path: '/office-admin/expenses', icon: DollarSign,label: 'GTP Bills' },
      { key: 'obligations',   path: '/obligations',           icon: Bell,      label: 'Obligations Tracker' },
      { key: 'equipment',     path: '/equipment',             icon: Wrench,    label: 'Equipment Service' },
      { key: 'franchise_invoices', path: '/franchise-invoices', icon: Receipt, label: 'Franchise Invoices' },
      { key: 'incidents',     path: '/incidents',             icon: ShieldAlert, label: 'Incident Management' },
    ],
  },

  warehouse_staff: {
    label: 'Warehouse', icon: Truck,
    items: [
      { key: 'receiving',       path: '/warehouse/receiving', icon: Truck,         label: 'Receiving' },
      { key: 'mobile_scanner',  path: '/inventory/scanner',   icon: BarChart3,     label: 'Mobile Scanner' },
      { key: 'product_expiry',  path: '/expiry-list',         icon: AlertTriangle, label: 'Product Expiry List' },
    ],
  },
};

// ──────────────────────────────────────────────────────────────────────
// Role → dropdown(s). Ordered; the first dropdown opens by default.
// ──────────────────────────────────────────────────────────────────────
export const ROLE_TO_DROPDOWNS = {
  ceo:                   ['leadership', 'operations'],
  md:                    ['leadership', 'operations'],
  tech_support:          ['tech'],
  operations_manager:    ['operations'],
  franchise_manager:     ['franchise'],
  hr_manager:            ['hr'],
  accounts_manager:      ['accounts_manager'],
  accounts_payables:     ['accounts_payables'],
  purchase_manager:      ['purchase'],
  warehouse_manager:     ['warehouse_manager'],
  warehouse_admin:       ['warehouse_admin'],
  warehouse_staff:       ['warehouse_staff'],
  warehouse_driver:      ['driver'],
  store_manager:         ['store_manager'],
  sales_manager:         ['sales'],
  marketing_manager:     ['marketing'],
  project_manager:       ['project_manager'],
  site_supervisor:       ['site_supervisor'],
  import_manager:        ['imports'],
  office_administrator:  ['office'],
  store_staff:           ['staff'],
};

// ──────────────────────────────────────────────────────────────────────
// Custom roles registry — populated at app boot from
// GET /api/access-control/custom-roles. Together with ROLE_TO_DROPDOWNS
// this is the SSOT for "what dropdowns does role X see?".
//
// Shape: { value: 'store_baker', label: 'Store Baker',
//          scope: 'store', dropdowns: ['staff'] }
// ──────────────────────────────────────────────────────────────────────
let _customRoles = [];

export function setCustomRoles(arr) {
  _customRoles = Array.isArray(arr) ? arr : [];
}

export function getCustomRoles() {
  return [..._customRoles];
}

export function getCustomRole(value) {
  return _customRoles.find((r) => r.value === value) || null;
}

/** Return the dropdown-keys list for a role (canonical OR custom). */
function _dropdownsForRole(role) {
  if (ROLE_TO_DROPDOWNS[role]) return ROLE_TO_DROPDOWNS[role];
  const cr = getCustomRole(role);
  return cr && Array.isArray(cr.dropdowns) ? cr.dropdowns : [];
}

/** Display label for a role — canonical first, then custom, then humanised key. */
export function labelForRole(role) {
  if (ROLE_LABELS[role]) return ROLE_LABELS[role];
  const cr = getCustomRole(role);
  if (cr && cr.label) return cr.label;
  return (role || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ──────────────────────────────────────────────────────────────────────
// Path → page title lookup. Used by the top Header so every page shows
// its own name (e.g. "Access Control") instead of the static "Dashboard"
// fallback baked into <Layout title="…" />.
// ──────────────────────────────────────────────────────────────────────
let _pathTitleCache = null;
function _buildPathTitleMap() {
  const map = {};
  // Common items
  for (const item of COMMON_ITEMS) {
    if (item.path && item.label) map[item.path] = item.label;
  }
  // Issue reporting
  if (ISSUE_REPORTING_ITEM.path) {
    map[ISSUE_REPORTING_ITEM.path] = ISSUE_REPORTING_ITEM.label;
  }
  // All dropdown items
  for (const key of Object.keys(DROPDOWNS)) {
    for (const item of DROPDOWNS[key].items || []) {
      if (item.path && item.label) map[item.path] = item.label;
    }
  }
  return map;
}

/** Resolve the human page title for a given pathname.
 *  Looks for an exact match first, then walks up path segments to find the
 *  longest registered prefix (so `/admin/user-management/123/edit` still
 *  resolves to "User Management"). Falls back to `defaultTitle` when no
 *  sidebar item maps to the path. */
export function titleForPath(pathname, defaultTitle = 'Dashboard') {
  if (!pathname) return defaultTitle;
  if (!_pathTitleCache) _pathTitleCache = _buildPathTitleMap();
  if (_pathTitleCache[pathname]) return _pathTitleCache[pathname];
  // Longest-prefix match
  const segs = pathname.split('/').filter(Boolean);
  for (let i = segs.length - 1; i > 0; i--) {
    const candidate = '/' + segs.slice(0, i).join('/');
    if (_pathTitleCache[candidate]) return _pathTitleCache[candidate];
  }
  return defaultTitle;
}




// `overrides` shape (loaded from /api/access-control/overrides):
//   { dropdowns: { <dropdown_key>: true|false },
//     items:     { '<dropdown_key>.<item_key>': true|false } }
//
// Semantics:
//   absent   → default (use static ROLE_TO_DROPDOWNS / DROPDOWNS config)
//   true     → GRANTED (force-show even if not in static config)
//   false    → BLOCKED (force-hide even if in static config)
//
// This means Tech Control Panel can both add (grant) and remove (block)
// access for any role × any panel/dropdown/item.
// ──────────────────────────────────────────────────────────────────────
export function getNavForRole(role, overrides = {}, ctx = {}) {
  const dropOv = overrides.dropdowns || {};
  const itemOv = overrides.items || {};
  const baseDropKeys = _dropdownsForRole(role);
  const baseDropSet = new Set(baseDropKeys);

  // Context-driven gating predicates (run after role + override filters).
  // `requiresFranchiseStore`: hide the item for store managers whose store
  // is not flagged as a franchise store in Locations.
  const passesCtx = (it) => {
    if (it?.requiresFranchiseStore) {
      if (['store_manager', 'grocery_manager'].includes(role)) {
        if (!ctx.isFranchiseStore) return false;
      }
    }
    return true;
  };

  // Final dropdown set = (base ∪ explicit grants) − explicit blocks.
  const grantedDropKeys = Object.keys(DROPDOWNS).filter(
    k => dropOv[k] === true && !baseDropSet.has(k)
  );
  const orderedDropKeys = [
    ...baseDropKeys.filter(k => dropOv[k] !== false && DROPDOWNS[k]),
    ...grantedDropKeys,
  ];
  const visibleDropSet = new Set(orderedDropKeys);

  const dropdowns = orderedDropKeys.map((dKey) => {
    const d = DROPDOWNS[dKey];
    if (!d) return null;

    // Default items for this role inside this dropdown — apply onlyRoles filter
    // ONLY if the dropdown is part of the role's base config; if Tech granted
    // the dropdown via override, the role is being given it as-is, but we
    // still respect onlyRoles so per-item author intent stays in place.
    const baseItems = d.items.filter(
      it => !it.onlyRoles || it.onlyRoles.includes(role)
    );
    const baseItemSet = new Set(baseItems.map(i => i.key));

    // Items granted explicitly for this role (true) — even if not in baseItems.
    const grantedItemKeys = d.items
      .filter(it => itemOv[`${dKey}.${it.key}`] === true && !baseItemSet.has(it.key))
      .map(it => it.key);

    const itemMap = Object.fromEntries(d.items.map(i => [i.key, i]));
    const finalItemKeys = [
      ...baseItems
        .filter(it => itemOv[`${dKey}.${it.key}`] !== false)
        .map(it => it.key),
      ...grantedItemKeys,
    ];

    const items = finalItemKeys
      .map(k => itemMap[k])
      .filter(Boolean)
      // Guardrail: ceo never sees System Health (tech_support only) — even if
      // the Tech dropdown is granted to ceo via override.
      .filter(it => !(role === 'ceo' && it.key === 'system_health'))
      .filter(passesCtx);

    return { key: dKey, label: d.label, icon: d.icon, items };
  }).filter(Boolean);

  // Orphan grants — items granted from dropdowns the user does NOT have.
  // These surface flat in the user's Common Items area so the grant is
  // actually reachable. Keys are namespaced to avoid clashing with real
  // common items (e.g. 'tasks') and to give unique React keys.
  const orphanItems = [];
  for (const [fk, v] of Object.entries(itemOv)) {
    if (v !== true) continue;
    if (fk.startsWith('common.')) continue;
    const dotIdx = fk.indexOf('.');
    if (dotIdx < 0) continue;
    const dKey = fk.slice(0, dotIdx);
    const iKey = fk.slice(dotIdx + 1);
    if (visibleDropSet.has(dKey)) continue; // already rendered inside its dropdown
    const dropdown = DROPDOWNS[dKey];
    const item = dropdown?.items.find(i => i.key === iKey);
    if (!item) continue;
    // Belt-and-braces: don't surface System Health to ceo even as an orphan.
    if (role === 'ceo' && item.key === 'system_health') continue;
    if (!passesCtx(item)) continue;
    orphanItems.push({
      ...item,
      key: `granted__${dKey}__${iKey}`, // unique vs COMMON_ITEMS
    });
  }

  // Common items: base ∪ common-granted ∪ orphan item-grants, − blocks.
  //
  // An explicit user/role GRANT (itemOv[`common.<key>`] === true) wins over
  // the static `hideForRoles` rule. Without this, items like `my_shifts`
  // (hidden for store_staff by default) silently ignore a Tech-granted
  // override — which broke the Access Control "Add access" workflow.
  const commonByKey = Object.fromEntries(COMMON_ITEMS.map(i => [i.key, i]));
  const baseCommonSet = new Set(COMMON_ITEMS.map(i => i.key));
  const grantedCommonKeys = Object.keys(itemOv)
    .filter(fk => fk.startsWith('common.') && itemOv[fk] === true)
    .map(fk => fk.slice('common.'.length))
    .filter(k => !baseCommonSet.has(k) && commonByKey[k]);

  const common = [
    ...COMMON_ITEMS
      .filter(it =>
        // Allow if not hidden for this role, OR if explicitly granted via override.
        itemOv[`common.${it.key}`] === true ||
        !(it.hideForRoles || []).includes(role)
      )
      .filter(it => itemOv[`common.${it.key}`] !== false),
    ...grantedCommonKeys.map(k => commonByKey[k]),
    ...orphanItems,
  ];

  // Issue Reporting: base ∪ grant − block.
  const baseIR = ROLES_WITH_ISSUE_REPORTING.includes(role);
  const irOv = itemOv['common.issue_reporting'];
  const showIR = irOv === false ? false : (baseIR || irOv === true);
  const issueReporting = showIR ? ISSUE_REPORTING_ITEM : null;

  return { common, dropdowns, issueReporting };
}

// Roles that oversee the entire org — sidebar is still curated by their
// dropdown config (so we don't dump every link in their nav), but the route
// guard must not block them from URL-deep-linking into any managed page.
// This prevents notification clicks (HR cert reminders, shift approvals,
// purchase reviews etc.) from getting bounced with "You do not have access".
const FULL_ACCESS_ROLES = ['ceo', 'md', 'tech_support'];

// Paths that any authenticated user can navigate to regardless of their
// sidebar — these are common notification destinations. The frontend trusts
// the backend to enforce per-row/per-record permission at the API layer
// (e.g., `/admin/staff` lists are scoped server-side). Without this list,
// notifications deep-linking to admin pages would bounce non-admin users
// even when they have a legitimate reason to land there (their own profile,
// their own shift, an expense they submitted, a project they're a member of).
const UNIVERSAL_ALLOWED_PATHS = [
  '/notifications', '/settings', '/login', '/signup', '/',
  // Notification destinations used by core/notifications.py + routes/*
  '/admin/staff', '/admin/users', '/admin/incidents', '/admin/certifications',
  '/admin/roster', '/admin/shift-approvals', '/admin/training-progress',
  '/admin/leave', '/admin/policies',
  '/payroll', '/projects', '/expenses', '/tasks', '/training', '/incidents',
  '/profile/certificates', '/my-shifts', '/gamification', '/dashboard',
  '/purchase/review',
];

// Hard guardrails: even full-access roles cannot URL-jump to these paths.
// Mirrors the sidebar visibility rules in getNavForRole (e.g., the
// "ceo never sees System Health" guardrail).
const ROLE_PATH_DENY = {
  ceo: ['/admin/system-health'],
  md:  ['/admin/system-health'],
};

// Flat list of every path the role can see — used by RoleGate to decide
// whether direct URL access is allowed.
export function allowedPathsForRole(role, overrides = {}, ctx = {}) {
  // Full-access roles get every managed path. Sidebar remains curated.
  if (FULL_ACCESS_ROLES.includes(role)) {
    const all = managedPaths();
    UNIVERSAL_ALLOWED_PATHS.forEach(p => all.add(p));
    (ROLE_PATH_DENY[role] || []).forEach(p => all.delete(p));
    return all;
  }

  const nav = getNavForRole(role, overrides, ctx);
  const paths = new Set();
  nav.common.forEach(i => paths.add(i.path));
  if (nav.issueReporting) paths.add(nav.issueReporting.path);
  nav.dropdowns.forEach(d => d.items.forEach(i => paths.add(i.path)));
  // Always-allowed paths (notifications, settings sub-pages, common
  // notification deep-link targets — backend gates the actual data).
  UNIVERSAL_ALLOWED_PATHS.forEach(p => paths.add(p));
  return paths;
}

// Every path that CAN be governed by access control — i.e. appears anywhere
// in COMMON_ITEMS / DROPDOWNS / ISSUE_REPORTING_ITEM.
//
// Used by the route-guard to decide whether a path is "managed" (subject to
// the resolved allowed-set for the user) or "free" (not a nav surface, so
// access control doesn't apply — e.g. /staff, /training, deep-linked detail
// pages, etc.). This keeps the form-driven access-control feature surgical:
// granting/blocking a sidebar item really does control its page reachability,
// without breaking unrelated pages that aren't in the sidebar at all.
export function managedPaths() {
  const paths = new Set();
  COMMON_ITEMS.forEach(i => paths.add(i.path));
  paths.add(ISSUE_REPORTING_ITEM.path);
  Object.values(DROPDOWNS).forEach(d => d.items.forEach(i => paths.add(i.path)));
  return paths;
}
